import { create } from 'zustand';
import type { FetchResourceHubListQuery } from '@edwin-group/people-graphql';
import type { PaginationInfo } from '../../shared/types';

export type ResourceCategoryNode = {
  documentId: string;
  category: string;
  color?: string | null;
  subCategories?: ResourceCategoryNode[];
};

export type ResourceHubItem = NonNullable<FetchResourceHubListQuery['resources']>['nodes'][number];

interface ResourceHubListState {
  resources: ResourceHubItem[];
  categories: ResourceCategoryNode[];
  rootCategories: ResourceCategoryNode[];
  categoryIdsWithResources: Set<string>;
  pagination: PaginationInfo;
  loading: boolean;
  searchQuery: string;
  selectedCategoryIds: string[];
  selectedSubcategoryIds: string[];
}

interface ResourceHubListActions {
  setResources: (resources: ResourceHubItem[]) => void;
  appendResources: (resources: ResourceHubItem[]) => void;
  setCategories: (categories: ResourceCategoryNode[]) => void;
  setRootCategories: (categories: ResourceCategoryNode[]) => void;
  setCategoryIdsWithResources: (ids: Set<string>) => void;
  setPagination: (pagination: PaginationInfo) => void;
  setLoading: (loading: boolean) => void;
  setSearchQuery: (query: string) => void;
  toggleCategory: (categoryId: string) => void;
  toggleSubcategory: (subcategoryId: string) => void;
  reset: () => void;
  resetFilters: () => void;
}

const initialState: ResourceHubListState = {
  resources: [],
  categories: [],
  rootCategories: [],
  categoryIdsWithResources: new Set<string>(),
  pagination: {
    page: 1,
    pageCount: 1,
    total: 0,
    pageSize: 9,
  },
  loading: false,
  searchQuery: '',
  selectedCategoryIds: [],
  selectedSubcategoryIds: [],
};

export const useResourceHubListStore = create<ResourceHubListState & ResourceHubListActions>((set) => ({
  ...initialState,
  setResources: (resources) => set({ resources }),
  appendResources: (resources) => set((state) => ({ resources: [...state.resources, ...resources] })),
  setCategories: (categories) => set({ categories }),
  setRootCategories: (rootCategories) => set({ rootCategories }),
  setCategoryIdsWithResources: (categoryIdsWithResources) => set({ categoryIdsWithResources }),
  setPagination: (pagination) => set({ pagination }),
  setLoading: (loading) => set({ loading }),
  setSearchQuery: (searchQuery) => set({ searchQuery }),
  toggleCategory: (categoryId) =>
    set((state) => ({
      selectedCategoryIds: state.selectedCategoryIds.includes(categoryId) ? [] : [categoryId],
      selectedSubcategoryIds: [],
    })),
  toggleSubcategory: (subcategoryId) =>
    set((state) => ({
      selectedSubcategoryIds: state.selectedSubcategoryIds.includes(subcategoryId)
        ? state.selectedSubcategoryIds.filter((id) => id !== subcategoryId)
        : [...state.selectedSubcategoryIds, subcategoryId],
    })),
  reset: () => set(initialState),
  resetFilters: () =>
    set({
      searchQuery: '',
      selectedCategoryIds: [],
      selectedSubcategoryIds: [],
    }),
}));
